import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { Star, X } from 'lucide-react';
import { projectsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { groupByOrg } from '@/lib/project-groups';
import { useToggleFavorite } from './ProjectSwitcher';
import { useCanFavorite } from '@/hooks/useCanFavorite';

/**
 * Favorites live behind a ★ on the app bar (Jesse, 2026-10-07): not in the
 * project dropdown, not in the nav. Clicking the star opens this panel beside
 * it: your favorite projects, grouped by organization, A–Z.
 *
 * The app bar only gives us a click, so the panel finds the star itself: the
 * real bar marks each registered item `[data-item-id="favorites"]` (the dev
 * mock does the same), and without an app bar the topbar fallback star is
 * `[data-favorites-anchor]`.
 */
export const useFavoritesPanel = create<{ open: boolean; toggle: () => void; close: () => void }>((set) => ({
  open: false,
  toggle: () => set((s) => ({ open: !s.open })),
  close: () => set({ open: false }),
}));

const ANCHOR = '[data-item-id="favorites"] button, [data-item-id="favorites"] a, [data-favorites-anchor]';
const WIDTH = 300;
const GUTTER = 12;

function findAnchor(): HTMLElement | null {
  return document.querySelector<HTMLElement>(ANCHOR);
}

export function FavoritesPanel() {
  const { open, close } = useFavoritesPanel();
  const { pathname } = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const toggleFavorite = useToggleFavorite();
  const currentProjectId = pathname.match(/^\/projects\/([^/]+)/)?.[1];
  // Favorites are for agency people (useCanFavorite); nothing for anyone else
  const canFavorite = useCanFavorite();

  const { data: projects } = useQuery({
    queryKey: ['projects', 'member'],
    queryFn: () => projectsApi.list(),
    enabled: open && canFavorite,
  });
  const groups = groupByOrg((projects ?? []).filter((p: any) => p.isFavorited && !p.archivedAt));

  // Close on navigation (picking a project is a navigation)
  useEffect(() => {
    close();
  }, [pathname, close]);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const a = findAnchor()?.getBoundingClientRect();
      const height = panelRef.current?.offsetHeight ?? 320;
      const maxHeight = window.innerHeight - 2 * GUTTER;
      if (!a) {
        setPos({ top: GUTTER + 56, left: window.innerWidth - WIDTH - GUTTER, maxHeight });
        return;
      }
      // Rail on the left (desktop): open to its right, bottom-aligned with the star.
      // Rail as a top bar (phones) or a topbar star: open below, kept on screen.
      const besideRail = a.left < 80 && window.innerWidth >= 1024;
      if (besideRail) {
        const top = Math.min(Math.max(GUTTER, a.bottom - height + 8), window.innerHeight - height - GUTTER);
        setPos({ top: Math.max(GUTTER, top), left: a.right + 10, maxHeight });
      } else {
        const left = Math.min(Math.max(GUTTER, a.right - WIDTH), window.innerWidth - WIDTH - GUTTER);
        setPos({ top: a.bottom + 8, left, maxHeight: window.innerHeight - a.bottom - 8 - GUTTER });
      }
    };
    place();
    const raf = requestAnimationFrame(place); // again once the list has rendered
    window.addEventListener('resize', place);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
    };
  }, [open, groups.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || findAnchor()?.contains(t)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      close();
      findAnchor()?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  if (!open || !canFavorite) return null;
  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Favorite projects"
      style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? 0, width: WIDTH, maxHeight: pos?.maxHeight, zIndex: 2147482000 }}
      className="flex flex-col overflow-hidden rounded-xl border border-grey-200 bg-white shadow-elevated"
    >
      <div className="flex items-center gap-2 border-b border-grey-100 px-4 py-3">
        <Star className="h-4 w-4 fill-warning text-warning" aria-hidden />
        <h2 className="flex-1 text-sm font-bold text-secondary">Favorites</h2>
        <button
          type="button"
          onClick={close}
          aria-label="Close favorites"
          className="rounded-md p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-1">
        {!projects ? (
          <p className="px-4 py-4 text-sm text-grey-500">Loading…</p>
        ) : groups.length === 0 ? (
          <p className="px-4 py-4 text-sm text-grey-500">
            No favorites yet. Star a project (☆ next to its name) to keep it here.
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.orgId}>
              <p className="truncate px-4 pb-0.5 pt-3 text-caption font-semibold text-grey-500">{g.orgName}</p>
              <ul>
                {g.projects.map((p: any) => {
                  const isCurrent = p.id === currentProjectId;
                  return (
                    <li key={p.id} className="group flex items-center pr-2">
                      <Link
                        to={`/projects/${p.id}`}
                        aria-current={isCurrent ? 'page' : undefined}
                        className={cn(
                          'min-w-0 flex-1 truncate py-1.5 pl-6 pr-2 text-sm transition-colors hover:bg-grey-50',
                          isCurrent ? 'font-semibold text-secondary' : 'text-secondary-600',
                        )}
                      >
                        {p.name}
                      </Link>
                      <button
                        type="button"
                        onClick={() => toggleFavorite.mutate(p)}
                        aria-label={`Remove ${p.name} from favorites`}
                        className="rounded p-1 text-warning opacity-60 transition-opacity hover:bg-grey-100 hover:opacity-100 focus:opacity-100"
                      >
                        <Star className="h-3.5 w-3.5 fill-warning" aria-hidden />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
      <div className="border-t border-grey-100 px-4 py-2">
        <Link to="/projects" className="text-sm font-medium text-accent hover:text-accent-700">
          All projects →
        </Link>
      </div>
    </div>,
    document.body,
  );
}
